<!--
// app/pages/[region]/[news]/index.vue
-->
<script setup lang="ts">
import type { Post } from "#shared/post/domain/models/Post";
import type { Ref } from "@vue/reactivity";
import type { I18nHeadMetaInfo } from "@nuxtjs/i18n";
import { useWebContext } from "~/composables/webContext";
import { useCompetitionStore } from "~/stores/competitionStore";
import GridNewsHero from "~/components/Shared/GridNewsHero.vue";
import { $FEATURED_ITEMS, $PER_PAGE_ITEMS } from "#shared/post/domain/service/PostService";
import CardNewsArticle from "~/components/Shared/news/CardNewsArticle.vue";
import PaginationNav from "~/components/Shared/PaginationNav.vue";
import { usePagination } from "~/composables/pages/pagination";
import { getWebContextCmsCategories } from "#shared/region/datasources/regionsData";
import { useLocalesStore } from "~/stores/localesStore";

//check validationNewsGuard middleware to add news param path for news page in new languages

definePageMeta({
  middleware: ["validation-news-guard", "not-found-king-of-kings-nations", "not-found-mena"],
});

const setI18nParams = useSetI18nParams();
const localesStore = useLocalesStore();

const localesWithNews = ref([]);

const checkAvailableNewsLocales = async () => {
  const locales = ["en", "es", "de", "fr", "it", "pt"];
  localesWithNews.value = locales.filter(
    locale => localesStore.currentRegionAvailableNewsLocales?.includes(locale) || false
  );
};
await checkAvailableNewsLocales();

setI18nParams({
  en: { news: localesWithNews.value?.includes("en") ? "news" : false },
  es: { news: localesWithNews.value?.includes("es") ? "actualidad" : false },
  de: { news: localesWithNews.value?.includes("de") ? "aktuelles" : false },
  fr: { news: localesWithNews.value?.includes("fr") ? "actualites" : false },
  it: { news: localesWithNews.value?.includes("it") ? "notizie" : false },
  pt: { news: localesWithNews.value?.includes("pt") ? "noticias" : false },
});

const { contextSiteTitle, webContext, contextDomain } = useWebContext();
const { region } = useCompetitionStore();
const { currentPage } = usePagination();
const localePath = useLocalePath();
const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();
const gridNewsKey = ref(0);

if (!localesWithNews.value?.includes(locale.value)) {
  throw createError({ statusCode: 404 });
}

const newsCategories = getWebContextCmsCategories(webContext);

const generalCategoryId = computed(() => {
  return !!newsCategories[region.id] ? newsCategories[region.id]["general"] : undefined;
});

const {
  data: posts,
  status,
  error,
} = await useFetch(`/api/cms/${region.id}/news`, {
  query: {
    per_page: $PER_PAGE_ITEMS,
    page: currentPage,
    lang: locale,
    categories: generalCategoryId,
  },
  watch: [currentPage, locale],
});

if (status.value === "success") gridNewsKey.value++;

if (error.value) {
  console.error(error.value);
}

const localeHead: Ref<I18nHeadMetaInfo> = useLocaleHead({
  seo: { canonicalQueries: ["page"] },
});

useHead({
  link: () => {
    return localeHead.value.link.map(link => {
      if (link.rel === "alternate" && link.href.includes("?page=")) {
        return {
          ...link,
          href: link.href.split("?page=")[0],
        };
      }
      return link;
    });
  },
});

useSeoMeta({
  title: t("footer.nav.news"),
  ogTitle: t("footer.nav.news"),
  description: t("meta.description.news.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.news.page", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <GridNewsHero is-news-page v-if="currentPage === 1" />

  <div
    class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 mt-12 mx-auto max-w-[1440px]"
    v-if="posts && posts.data.length"
    :key="gridNewsKey"
  >
    <card-news-article
      v-for="post in currentPage === 1 ? posts.data.slice($FEATURED_ITEMS) : posts.data"
      :post="<Post>post"
      :key="post.id"
    ></card-news-article>
  </div>

  <div v-if="posts" class="py-6 flex justify-center items-center" :key="posts.totalItems">
    <pagination-nav
      v-model="currentPage"
      :total-items-amount="posts.totalItems"
      :items-per-page="$PER_PAGE_ITEMS"
      scroll-to-top
    />
  </div>
</template>

<style scoped></style>
